import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDemo } from '../../state/DemoContext';
import { formatCurrency, formatDate, getDaysUntilExpiry, calculateCommitmentPercentage } from '../../utils/helpers';
import LtvBadge from './LtvBadge';
import EncumbranceBadge from './EncumbranceBadge';
import './listingcard.css';

const ListingCard = ({ listing, property }) => {
  const { state, actions } = useDemo();
  const history = useHistory();

  const daysUntilExpiry = getDaysUntilExpiry(listing.expiresAt);
  const commitmentPercentage = calculateCommitmentPercentage(listing.committedAmount, listing.requestedAmount);
  const isExpired = daysUntilExpiry <= 0;
  const isFullyFunded = listing.status === 'Matched';

  const handleViewDetails = () => {
    history.push(`/jo-xyz-demo/asset/${listing.id}`);
  };

  const handleCommit = (e) => {
    e.stopPropagation();
    if (state.currentInvestor) {
      actions.showModal('commit', { listing, property });
    } else {
      actions.showToast('Please sign up as an investor first', 'warning');
      history.push('/jo-xyz-demo/investor/signup');
    }
  };

  const getStatusColor = () => {
    switch (listing.status) {
      case 'Open':
        return isExpired ? '#EF4444' : '#6EE7F2';
      case 'Matched':
        return '#22C55E';
      case 'Closed':
        return '#A3B2C5';
      default:
        return '#F59E0B';
    }
  };

  return (
    <div
      className={`jo-xyz-demo-listing-card ${isExpired ? 'jo-xyz-demo-listing-card--expired' : ''} ${isFullyFunded ? 'jo-xyz-demo-listing-card--funded' : ''}`}
      onClick={handleViewDetails}
    >
      <div className="jo-xyz-demo-card">
        <div className="jo-xyz-demo-listing-card__header">
          <div className="jo-xyz-demo-listing-card__title">
            <h3>{listing.title}</h3>
            <div className="jo-xyz-demo-listing-card__location">
              📍 {listing.location}
            </div>
          </div>
          <div className="jo-xyz-demo-listing-card__status">
            <span
              className="jo-xyz-demo-listing-card__status-badge"
              style={{ backgroundColor: getStatusColor() }}
            >
              {listing.status}
            </span>
          </div>
        </div>

        <div className="jo-xyz-demo-listing-card__content">
          <div className="jo-xyz-demo-listing-card__amount">
            <div className="jo-xyz-demo-amount-display">
              <span className="jo-xyz-demo-amount-display__label">Requested Amount</span>
              <span className="jo-xyz-demo-amount-display__value">
                {formatCurrency(listing.requestedAmount)}
              </span>
            </div>
            <div className="jo-xyz-demo-amount-display">
              <span className="jo-xyz-demo-amount-display__label">Term</span>
              <span className="jo-xyz-demo-amount-display__value">
                {listing.desiredTermMonths} months
              </span>
            </div>
          </div>

          <div className="jo-xyz-demo-listing-card__badges">
            <LtvBadge ltv={listing.ltv} size="small" />
            <EncumbranceBadge
              lienPosition={property?.lienPosition}
              encumbrances={property?.encumbrances}
              inUnderwriting={property?.inUnderwriting}
              size="small"
            />
          </div>

          <div className="jo-xyz-demo-listing-card__progress">
            <div className="jo-xyz-demo-progress-header">
              <span className="jo-xyz-demo-progress-label">Funding Progress</span>
              <span className="jo-xyz-demo-progress-percentage">
                {commitmentPercentage.toFixed(0)}%
              </span>
            </div>
            <div className="jo-xyz-demo-progress-bar">
              <div
                className="jo-xyz-demo-progress-bar__fill"
                style={{ width: `${commitmentPercentage}%` }}
              />
            </div>
            <div className="jo-xyz-demo-progress-details">
              <span className="jo-xyz-demo-progress-committed">
                {formatCurrency(listing.committedAmount)} committed
              </span>
              <span className="jo-xyz-demo-progress-investors">
                {listing.investorCount} investor{listing.investorCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div className="jo-xyz-demo-listing-card__meta">
            <div className="jo-xyz-demo-meta-item">
              <span className="jo-xyz-demo-meta-label">Created</span>
              <span className="jo-xyz-demo-meta-value">{formatDate(listing.createdAt)}</span>
            </div>
            <div className="jo-xyz-demo-meta-item">
              <span className="jo-xyz-demo-meta-label">Expires</span>
              <span className={`jo-xyz-demo-meta-value ${isExpired ? 'jo-xyz-demo-meta-value--expired' : ''}`}>
                {isExpired ? 'Expired' : `${daysUntilExpiry} days`}
              </span>
            </div>
          </div>

          <div className="jo-xyz-demo-listing-card__actions">
            <button
              className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary jo-xyz-demo-btn--sm"
              onClick={handleViewDetails}
            >
              View Details
            </button>
            {!isExpired && !isFullyFunded && (
              <button
                className="jo-xyz-demo-btn jo-xyz-demo-btn--primary jo-xyz-demo-btn--sm"
                onClick={handleCommit}
              >
                Commit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
